import * as React from 'react';
import { useEffect } from 'react';
import { Activity, PublicAmorgUser, UserToken } from "../../../amorg-common";
import { getActivity } from '../../../api/activityClientApi';
import { getAuthorizedActiveUserOrRedirect } from '../../../api/serverApiHelpers';
import { getUsers } from '../../../api/userClientApi';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { ActivityComponentProps } from './ActivityComponentProps';
import { ActivityHeader } from './ActivityHeader';
import { AgendaItemListPanel } from './AgendaItemListPanel';
import { MeetingDetailsPanel } from './MeetingDetailsPanel';

type MeetingActivityPageViewProps = {
    activityId:string,
    onActivityTitleChanged:(title:string) => void,
}

type State = 
    { state:'initial-loading' } |
    { state:'ready', activity:Activity, activeUser:UserToken, users:PublicAmorgUser[], loading: boolean } |
    { state:'error', errorMessage:string }

export default function MeetingActivityPageView(props:MeetingActivityPageViewProps): JSX.Element 
{
    const [state, setState] = React.useState<State>({ state:'initial-loading' });

    useEffect(() => {
        updateState();
    }, [props.activityId]);

    async function updateState()
    {
        if(state.state === 'ready') {
            setState({...state, loading:true});
        }

        const activity = await getActivity(props.activityId);
        if(!activity)
        {
            setState({state:'error', errorMessage:'Ugyldig url'});
            return;
        }

        if(activity.activityType !== 'WorkgroupMeeting' && activity.activityType !== 'AnnualWorkgroupStatus')
        {
            setState({state:'error', errorMessage:'Ugyldig aktivitetstype'});
            return;
        }

        props.onActivityTitleChanged(activity.title);
        
        const activeUser = await getAuthorizedActiveUserOrRedirect(['EditSurveys']);

        setState({
            state:'ready',
            activity,
            activeUser,
            users: await getUsers(),
            loading:false
        });
    }

    let componentProps: ActivityComponentProps | null = null;
    if(state.state === 'ready')
    {
        componentProps = {
            activeUser: state.activeUser,
            activity: state.activity as Activity,
            users: state.users,
            onActivityUpdated:updateState
        };
    }

    return (
        <>
            <LoadingPageOverlay loading={state.state === 'initial-loading' || (state.state === 'ready' && state.loading)} />

            {state.state === 'error' && <p>{state.errorMessage}</p>}

            {state.state === 'ready' && 
                <AgendaMeetingActivityView {...(componentProps as ActivityComponentProps)} />
            }
        </>
    );
}

function AgendaMeetingActivityView (props: ActivityComponentProps): JSX.Element 
{
    const [loading, setLoading] = React.useState(true);

    async function isLoading (value: boolean)
    {
        setLoading(value);
    }

    async function handleActivityUpdated ()
    {
        setLoading(true);
        props.onActivityUpdated();
        // setInvitationStatus(await getMeetingInvitationStatus(props.activity.id));
        setLoading(false);
    }

    return (
        <>
            
            <ActivityHeader
                activity={props.activity}
                deleteConfirmationTextInModal="Tilhørende handlepunkter vil ligeledes blive slettet."
                onActivityUpdated={handleActivityUpdated}
                isLoading={isLoading} />

            <SingleColumnLayout>
                <>

                    <MeetingDetailsPanel
                        activity={props.activity}
                        descriptionText="Mødebeskrivelse"
                        periodSelection='date-time'
                        onActivityUpdated={handleActivityUpdated}
                        componentTitle='Møde info'
                        users={props.users}
                        participantTexts={{ header: 'Deltagere' }}
                        infoComponents={
                            <>
                                <LoadingPageOverlay loading={loading} />
                            </>
                        } />
                
                    <AgendaItemListPanel
                        activityId={props.activity.id}
                        agendaItems={props.activity.agendaItems}
                        users={props.users}
                        onUpdated={handleActivityUpdated} />
                </>
            </SingleColumnLayout>
        </>
    );
}